<script setup lang="ts">
import { Link, usePage } from "@inertiajs/vue3";
import LanguageSelector from '@/Components/LanguageSelector.vue';
import { ref } from 'vue';

const page = usePage();
const locale = page.props.locale || 'ro';
const isMobileMenuOpen = ref(false);

const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const localizedPath = (path: string) => {
    const currentLocale = usePage().props.locale;
    
    // Remove any existing /en prefix
    const cleanPath = path.replace(/^\/en/, '');
    
    // For English, add /en prefix
    if (currentLocale === 'en') {
        // Special case for root path
        if (cleanPath === '/') {
            return '/en';
        }
        return `/en${cleanPath}`;
    }
    
    // For Romanian, use the clean path
    return cleanPath;
};

</script>

<template>
    <div class="min-h-screen flex flex-col">
        <header class="fixed top-0 left-0 right-0 bg-white z-50">
            <div class="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center relative">
                <div class="flex items-center justify-between w-full">
                    <!-- Logo -->
                    <div class="flex items-center">
                        <Link :href="localizedPath('/')" class="flex items-center">
                            <img src="/images/logo-seeo2.svg" alt="SeeO2 Logo" class="brand-image mr-2">
                        </Link>
                    </div>

                    <!-- Center Navigation (Desktop) -->
                    <ul class="hidden lg:flex space-x-12">
                        <li>
                            <Link :href="localizedPath('/')" class="hover:text-teal-600">{{ $t('public.navigation.home') }}</Link>
                        </li>
                        <li>
                            <Link :href="localizedPath('/esg')" class="hover:text-teal-600">{{ $t('public.navigation.esg') }}</Link>
                        </li>
                        <li>
                            <Link :href="localizedPath('/csrd')" class="hover:text-teal-600">{{ $t('public.navigation.csrd') }}</Link>
                        </li>
                        <li>
                            <Link :href="localizedPath('/book-meeting')" class="hover:text-teal-600">{{ $t('public.book_meeting.title') }}</Link>
                        </li>
                    </ul>

                    <!-- Right Side Items -->
                    <div class="flex items-center space-x-4">
                        <LanguageSelector />
                        <!-- Login (Desktop) -->
                        <Link 
                            href="/login" 
                            class="hidden lg:inline-flex items-center px-4 py-2 bg-teal-700 text-white font-light rounded-full shadow hover:bg-teal-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                        >
                            {{ $t('public.navigation.login') }}
                            <span class="ml-2 inline-flex items-center justify-center h-6 w-6 rounded-full bg-lime-200">
                                <i class="fas fa-user text-teal-700"></i>
                            </span>
                        </Link>
                        <!-- Hamburger (Mobile) -->
                        <button 
                            @click="toggleMobileMenu"
                            class="lg:hidden p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        >
                            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path v-if="!isMobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
                                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>
                </div>

                <!-- Mobile Menu (Dropdown) -->
                <div 
                    v-show="isMobileMenuOpen" 
                    class="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg mt-2 py-2 z-50"
                >
                    <div class="px-4 py-2 space-y-4">
                        <Link 
                            v-for="(link, index) in [
                                { href: '/', text: 'public.navigation.home' },
                                { href: '/esg', text: 'public.navigation.esg' },
                                { href: '/csrd', text: 'public.navigation.csrd' },
                                { href: '/book-meeting', text: 'public.book_meeting.title' }
                            ]"
                            :key="index"
                            :href="localizedPath(link.href)"
                            class="block py-2 hover:text-teal-600"
                            @click="isMobileMenuOpen = false"
                        >
                            {{ $t(link.text) }}
                        </Link>
                        <div class="pt-2 border-t">
                            <Link 
                                href="/login" 
                                class="block w-full text-center px-4 py-2 bg-teal-700 text-white font-light rounded-full shadow hover:bg-teal-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                @click="isMobileMenuOpen = false"
                            >
                                {{ $t('public.navigation.login') }}
                                <span class="ml-2 inline-flex items-center justify-center h-6 w-6 rounded-full bg-lime-200">
                                    <i class="fas fa-user text-teal-700"></i>
                                </span>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </header>
        <main class="flex-1 mt-[72px]">
            <div class="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
                <slot />
            </div>
        </main>
    </div>
</template>

<style scoped>
.brand-image {
    height: 40px;
}

/* Add smooth transition for mobile menu */
.lg\:hidden {
    transition: all 0.3s ease-in-out;
}
</style>
